import { defineStore } from 'pinia'
import axios from '@/services/index.js'
import { useCompanyStore } from './company'
import { useBranchofficesStore } from './branchoffices'

export const useMeStore = defineStore('me', {
  state: () => ({
    loading: false,
    user: {
      id: null,
      name: '',
      email: '',
      company_id: null,
      branchoffice_id: null,
      role_id: null,
      company_db: '',
      permissions: [],
      last_week: false,
      expired: false,
      OWNER: 0
    }
  }),

  getters: {
    // Getter principal - siempre retorna un objeto válido
    getUser: (state) => {
      return state.user || {}
    },
    
    // Getter para verificar si el usuario está cargado
    isLoaded: (state) => {
      return state.user && state.user.id !== null
    },
    
    // Getter para permisos - siempre retorna array
    permissions: (state) => {
      return state.user?.permissions || []
    },
    
    // Getter para verificar permisos específicos
    hasPermission: (state) => {
      return (permission) => {
        return state.user?.permissions?.includes(permission) || false
      }
    },
    
    // Getter para verificar si mostrar last week (owner)
    showLastweek: (state) => {
      return state.user?.last_week && state.user?.OWNER === 1
    },
    
    // Getter para configuraciones de formato de moneda
    configurations: (state) => {
      return state.user?.configurations || {
        CURRENCY_DECIMAL_SEPARATOR: ',',
        CURRENCY_THOUSAND_SEPARATOR: '.',
        CURRENCY_DECIMAL_DIGITS: '2'
      }
    }
  },

  actions: {
    async fetchUser() {
      this.loading = true
      try {
        // Verificar que hay token antes de hacer la petición
        const token = localStorage.getItem('token')
        if (!token) {
          console.log('⚠️ No token found, skipping user fetch')
          return null
        }
        
        console.log('🔄 Fetching user data...')
        // Mismo estado que devuelven el login y el registro (User::authState)
        const response = await axios.get('/api/users/me')

        if (!response.data || !response.data.user) {
          console.warn('⚠️ Empty response from API')
          return null
        }

        const userData = response.data.user
        console.log('✅ User data received:', userData)
        
        // Separar datos en sus respectivos stores
        const { branchoffice, company, configurations, ...userInfo } = userData
        
        // Guardar datos del usuario
        this.setUser(userInfo)
        console.log('👤 User data saved to store')
        
        // Enviar datos de empresa al store company
        if (company || configurations) {
          const companyStore = useCompanyStore()
          companyStore.setCompanyData({ company, configurations })
          console.log('🏢 Company data saved to store')
        }
        
        // Enviar datos de sucursal al store branchoffices
        if (branchoffice) {
          const branchofficesStore = useBranchofficesStore()
          branchofficesStore.setCurrentBranchoffice(branchoffice)
          console.log('🏬 Branchoffice data saved to store')
        }
        
        return userInfo
      } catch (error) {
        console.error('❌ Error fetching user:', error)
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        })
        throw error
      } finally {
        this.loading = false
      }
    },
    
    /*
     * Ya no hace falta armar el usuario a mano desde un "metadata": el login,
     * el registro y GET /api/users/me devuelven el mismo objeto (User::authState
     * en el backend), así que los tres llaman directo a setUser().
     *
     * Antes cada fuente mandaba un shape distinto y este método leía campos que
     * el backend nunca enviaba (branchoffice_id, role_id, expired, last_week y
     * company_db, del viejo modelo de una base por empresa). Los dos que sí se
     * usaban venían mal: permissions llegaba vacío y OWNER no llegaba, lo que
     * dejaba la pantalla de facturación bloqueada incluso para el dueño.
     */

    setUser(userData) {
      this.user = userData
      
      // Sincronizar con localStorage
      if (userData.permissions) {
        localStorage.setItem('permissions', JSON.stringify(userData.permissions))
      }
      if (userData.expired !== undefined) {
        localStorage.setItem('expired', userData.expired)
      }
    },
    
    updatePermissions(permissions) {
      if (this.user) {
        this.user.permissions = permissions
        localStorage.setItem('permissions', JSON.stringify(permissions))
      }
    },
    
    /**
     * Cierra la sesión.
     *
     * Primero revoca el token en el servidor: borrarlo solo del navegador lo
     * deja válido en la base, así que una copia capturada seguiría entrando
     * después de "cerrar sesión". Si esa llamada falla, la sesión local se
     * cierra igual — quien pidió salir tiene que salir.
     *
     * Es el único punto de logout de la app: antes había cuatro
     * (el sidebar, la ruta /logout, el interceptor 401 y App.vue), cada uno
     * limpiando un subconjunto distinto del estado.
     */
    async logout() {
      try {
        await axios.post('/logout')
      } catch (error) {
        console.warn('No se pudo revocar el token en el servidor:', error.message)
      }

      this.clearSession()
    },

    /** Borra todo rastro local de la sesión, sin tocar el servidor. */
    clearSession() {
      this.clearUser()
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
    },

    // Limpiar usuario (logout)
    clearUser() {
      this.user = {
        id: null,
        name: '',
        email: '',
        company_id: null,
        branchoffice_id: null,
        role_id: null,
        company_db: '',
        permissions: [],
        last_week: false,
        expired: false,
        OWNER: 0
      }

      // Limpiar localStorage
      localStorage.removeItem('permissions')
      localStorage.removeItem('expired')
    },

    setLoading(value) {
      this.loading = value
    },

    // Recuperación de contraseña
    async requestPasswordReset(email) {
      this.loading = true
      try {
        await axios.post(
          `${import.meta.env.VITE_APP_ROOT_API}password/email`,
          { email }
        )
        return {
          success: true,
          message: 'Te hemos enviado un correo con las instrucciones para recuperar tu contraseña.'
        }
      } catch (error) {
        let errorMessage = 'Ocurrió un error. Por favor intenta nuevamente.'

        if (error.response?.status === 404) {
          errorMessage = 'No encontramos una cuenta asociada a este correo.'
        } else if (error.response?.status === 429) {
          errorMessage = 'Has realizado demasiados intentos. Por favor espera unos minutos.'
        }

        throw new Error(errorMessage)
      } finally {
        this.loading = false
      }
    }
  }
})